
import React from 'react';
import { motion } from 'framer-motion';
import { CloudUpload, Image, FileText, NotebookTabs, ArrowRight, Sparkles, Bell } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const EmptyNotifications: React.FC = () => {
  const navigate = useNavigate();

  const contentTypes = [
    {
      title: 'Create Project',
      description: 'Get notifications when AI generates tasks, milestones, and project updates',
      icon: NotebookTabs,
      route: '/task-manager',
      gradient: 'from-purple-500 to-pink-500',
      bgGradient: 'from-purple-50 to-pink-50',
      darkBgGradient: 'dark:from-purple-950/50 dark:to-pink-950/50',
      iconColor: 'text-purple-600 dark:text-purple-400',
      delay: 0.1,
    },
    {
      title: 'Research Topics',
      description: 'Receive alerts when research is complete and ready for review',
      icon: FileText,
      route: '/research-generator',
      gradient: 'from-emerald-500 to-teal-500',
      bgGradient: 'from-emerald-50 to-teal-50',
      darkBgGradient: 'dark:from-emerald-950/50 dark:to-teal-950/50',
      iconColor: 'text-emerald-600 dark:text-emerald-400',
      delay: 0.2,
    },
    {
      title: 'Generate Images',
      description: 'Get notified when your image generation jobs finish processing',
      icon: Image,
      route: '/image-generator',
      gradient: 'from-blue-500 to-cyan-500',
      bgGradient: 'from-blue-50 to-cyan-50',
      darkBgGradient: 'dark:from-blue-950/50 dark:to-cyan-950/50',
      iconColor: 'text-blue-600 dark:text-blue-400',
      delay: 0.3,
    },
  ];

  const handleNavigate = (route: string) => {
    navigate(route);
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] px-6 py-12">
      {/* Main Icon and Title */}
      <motion.div
        initial={{ scale: 0.8, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 100, damping: 15 }}
        className="text-center mb-12"
      >
        <div className="mb-4">
          <div className="inline-block mx-auto bg-white/10 dark:bg-white/5 rounded-2xl border border-white/20 dark:border-white/10 backdrop-blur-sm shadow-lg p-3">
            <img 
              src="/images/finalPiece_logo.png" 
              alt="Final Piece Logo" 
              className="w-32 h-auto object-contain"
            />
          </div>
        </div>
        
        <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
          No Notifications Yet
        </h2>
        <p className="text-lg text-slate-600 dark:text-slate-300 max-w-md mx-auto">
          Start using our systems and we'll keep you updated with progress notifications
        </p>
      </motion.div>

      {/* Action Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full max-w-4xl mb-8">
        {contentTypes.map((type, index) => (
          <motion.div
            key={type.title}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ 
              delay: type.delay,
              type: 'spring',
              stiffness: 100,
              damping: 15 
            }}
            className="group"
          >
            <div className={`
              relative p-6 rounded-2xl border border-slate-200/60 dark:border-slate-700/60 
              bg-gradient-to-br ${type.bgGradient} ${type.darkBgGradient}
              hover:shadow-xl hover:shadow-slate-200/50 dark:hover:shadow-slate-900/50
              transition-all duration-300 cursor-pointer
              hover:scale-[1.02] hover:-translate-y-1
            `}
            onClick={() => handleNavigate(type.route)}
            >
              {/* Gradient overlay on hover */}
              <div className={`
                absolute inset-0 rounded-2xl bg-gradient-to-br ${type.gradient} 
                opacity-0 group-hover:opacity-5 transition-opacity duration-300
              `} />
              
              {/* Content */}
              <div className="relative">
                <div className="flex items-center justify-between mb-4">
                  <div className={`
                    flex items-center justify-center w-12 h-12 rounded-xl
                    bg-white/80 dark:bg-slate-800/80 shadow-sm
                    group-hover:scale-110 transition-transform duration-300
                  `}>
                    <type.icon className={`w-6 h-6 ${type.iconColor}`} />
                  </div>
                  <ArrowRight className={`
                    w-5 h-5 ${type.iconColor} opacity-0 group-hover:opacity-100 
                    transform translate-x-2 group-hover:translate-x-0 
                    transition-all duration-300
                  `} />
                </div>
                
                <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
                  {type.title}
                </h3>
                <p className="text-slate-600 dark:text-slate-300 text-sm leading-relaxed">
                  {type.description}
                </p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

    </div>
  );
};

export default EmptyNotifications;
